import { ButtonText } from '../myusernotifications/add.edit.notifications/buttontext';
import { Component, inject, OnInit, input, output, viewChild } from '@angular/core';
import { SelectItem } from 'primeng/api';
import { SubGroupsService, ModalService, OrganizationService, UserService } from '../../../../services';
import { SubGroup, Market, User, Organization, UserAccessEnum } from '../../../../types';
import { NgForm, FormsModule } from '@angular/forms';
import { concatMap, EMPTY, forkJoin, of, switchMap, takeUntil, tap } from 'rxjs';
import { OrgStatusPipe } from '../../../../pipes/orgstatus.pipe';
import { CommandButtonComponent } from '../../../pcpcommandbar/commandbutton/commandbutton.component';
import { PcpCommandBarComponent } from '../../../pcpcommandbar/pcp-commandbar.component';
import { UnsubscribeOnDestroy } from '../../../../classes';
import { TableModule } from 'primeng/table';


@Component({
    selector: 'myusersettings',
    templateUrl: './myusersettings.component.html',
    styleUrls: ['./myusersettings.component.scss'],
    standalone: true,
    imports: [FormsModule, PcpCommandBarComponent, CommandButtonComponent, OrgStatusPipe, TableModule]
})
export class MyUserSettingsComponent extends UnsubscribeOnDestroy implements OnInit {
    subGroupList: SubGroup[] = [];
    market: Market;
    communities: SelectItem[];
    selectedSubGroup: SubGroup = null;
    user: User = new User();
    selectedSubGroupID: number = 0;
    originalSubGroupID: number = 0;
    userGuid: string;
    isAppWide: boolean = false;
    marketList: Organization[] = [];
    subGroupCommunities: Organization[] = [];
    displayMarketCommunities: boolean = false;
    displayMySchedules: boolean = false;
    isSaveClicked: boolean = false;
    isInitialLoad: boolean = false;

    readonly appWideRoleStatus = input<boolean>(undefined);
    readonly appWideEvent = output<boolean>();

    readonly userSettingsForm = viewChild<NgForm>("userSettingsForm");

    private subGroupsService = inject(SubGroupsService);
    private modalService = inject(ModalService);
    private orgService = inject(OrganizationService);
    private userService = inject(UserService);

    constructor() { super(); }

    ngOnInit() {
        this.isAppWide = this.appWideRoleStatus();
        this.isInitialLoad = true;
        this.loadSubGroupsForUser$()
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe((results) => {
                this.setSubGroupCommunities(results.subGroup);
            });
    }

    loadSubGroupsForUser$() {
        return this.userService.getMyUserProfile()
            .pipe(
                switchMap((user) => {
                    this.user = <User>user;

                    //Use a separate variable for Markets so that when a User has no assigned Orgs (meaning they're technically assigned to them all)
                    //the application will display all markets but won't create new UserOrg records when saving to the database
                    this.marketList = this.user.userOrgs;

                    if (this.user.subGroup) {
                        this.selectedSubGroupID = this.user.subGroup.subGroupID;
                        this.originalSubGroupID = this.user.subGroup.subGroupID;
                    }

                    return forkJoin({
                        subGroup: this.getSubGroup$(this.originalSubGroupID),
                        getAvailable: this.getAvailableSubgroupsForUser$()
                    });
                })
            )
    }

    getAvailableSubgroupsForUser$() {
        return this.orgService.getActiveMarkets()
            .pipe(
                takeUntil(this.ngUnsubscribe$),
                switchMap((mkts: Market[]) => {
                    this.assignOrgNamesForMarketList(mkts);

                    //user will not have any orgs if they are assigned to orgAll in AD
                    if (this.marketList.length === 0) {
                        this.marketList = mkts;
                    }

                    this.sortMarketList();

                    //Get all Subgroups within the User's assigned Orgs
                    let marketIds: number[] = this.marketList.map((m) => m.orgId);
                    if (!this.isAppWide)
                        return this.subGroupsService.getSubGroups(marketIds);
                    else
                        return of(undefined);
                }),
                tap((subgroups: SubGroup[]) => {
                    if (subgroups.length > 0) {
                        this.subGroupList = subgroups;
                        this.subGroupList.forEach((subGroup) => {
                            subGroup.divisionName = this.marketList.find((mkt) => mkt.orgId === subGroup.divisionID).orgName;
                        });
                    }

                    this.displayMarketCommunities = false;
                    this.displayMySchedules = this.user.role && !this.isAppWide && this.marketList.length === 1 ? true : false;
                })
            )
    }

    assignOrgNamesForMarketList(mkts: Organization[]): void {
        this.marketList.forEach((org: Organization) => {
            const market = mkts.find((mkt) => mkt.orgId === org.orgId);
            org.orgName = market.orgName;
        });
    }

    sortMarketList(): void {
        //Sort Markets in alphabetical order
        this.marketList = this.marketList.sort((a, b) => {
            if (a.orgName < b.orgName) { return -1; }
            else if (a.orgName > b.orgName) { return 1; }
            else { return 0; }
        });
    }

    private setSubGroupCommunities(subGroup: SubGroup) {
        if (!subGroup) return;

        this.subGroupCommunities = subGroup.communities;
        this.subGroupCommunities.sort((a, b) => (a.orgName > b.orgName) ? 1 : ((b.orgName > a.orgName) ? -1 : 0));
        this.isSaveClicked = false;
        this.isInitialLoad = false;
    }

    getSubGroup$(subGroupId: number) {
        return subGroupId > 0 ? this.subGroupsService.getSubGroup(subGroupId) : of(undefined);
    }

    getSubGroupCommunities(subGroupId: number) {
        this.subGroupCommunities = [];

        this.getSubGroup$(subGroupId)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe((subGroup) => {
                this.setSubGroupCommunities(subGroup);
            });
    }

    saveUpdatedSubgroup(): void {
        this.user.subGroup = null;
        if (this.selectedSubGroupID > 0) {
            this.user.subGroup = this.subGroupList.find((sg) => sg.subGroupID === this.selectedSubGroupID);
        }

        this.userService.saveUser(this.user, UserAccessEnum.SaveMyProfile)
            .pipe(
                takeUntil(this.ngUnsubscribe$),
                concatMap(() => {
                    this.modalService.showSuccessModal();
                    return this.loadSubGroupsForUser$();
                })
            )
            .subscribe({
                next: () => {
                    this.isSaveClicked = true;
                    this.userSettingsForm().form.markAsPristine();
                },
                error: (err) => this.modalService.showModal({ "content": "ERROR! - " + err, "type": "error", "autodismiss": true })
            });

    }

    cancelUserSettingChanges(): void {
        if (this.selectedSubGroupID > 0 && this.subGroupCommunities.length > 0) {
            this.modalService.showWarningModal("Are you sure you want to discard changes?")
                .pipe(
                    takeUntil(this.ngUnsubscribe$),
                    switchMap((isYesClicked: boolean) => {
                        if (!isYesClicked) return EMPTY;

                        this.selectedSubGroupID = this.originalSubGroupID;
                        this.subGroupCommunities = [];
                        return this.getSubGroup$(this.selectedSubGroupID);
                    })
                )
                .subscribe((subGroup) => {
                    this.setSubGroupCommunities(subGroup)
                    this.userSettingsForm().form.markAsPristine();
                });
        }
    }
}
